import React from 'react'
import { Container, Table, Row, Col, Label } from 'reactstrap'
import { getCapacityStats, getInProgressTasks, getEmployeeWeeklyStats, getWipStats, getPaintColorCount, getTodaysScheduleByEquip, getQueuedScheduleByEquip } from '../model/request/schedule'
import { InfoButton } from '../view/InfoButton'
import { JustDate } from '../model/JustDate'
import { dateOffsetText } from '../model/Formatting'
const { getAllEquips } = require('../model/request/equip')

// Reused/cycled per job - color scheme is a placeholder default, not a
// final decision (see TODO.md's machine-availability-calendar item -
// the actual color scheme still needs discussing/deciding).
const CALENDAR_COLORS = ['#4CAF50', '#2196F3', '#F44336', '#FFC107', '#E91E63', '#FF9800']
const CALENDAR_WINDOW_START_HOUR = 4
const CALENDAR_WINDOW_END_HOUR = 28 // 4am the next day - see renderMachineCalendar

function colorForJob(quoteId) {
    let hash = 0
    for (let i = 0; i < quoteId.length; i++) {
        hash = (hash * 31 + quoteId.charCodeAt(i)) & 0xffffffff
    }
    return CALENDAR_COLORS[Math.abs(hash) % CALENDAR_COLORS.length]
}

/**
 * Effective [start, end] for a task's calendar bar, filling in the gaps
 * the raw data leaves:
 * - No start_time: assume it started 10 minutes before end_time.
 * - No end_time (still in progress / not marked complete): assume it'll
 *   end at max(start + expected duration, now + 10 minutes) - so a task
 *   already running longer than expected still shows as ending shortly
 *   after "now," not stuck in the past.
 * Neither start_time nor end_time (task hasn't started or finished) is
 * not expected to reach here at all - the query only returns rows where
 * one of the two actually falls on today.
 */
function computeTaskSpan(task, now) {
    const FALLBACK_MIN = 10
    const endTime = task.end_time ? new Date(task.end_time) : null
    let start = task.start_time
        ? new Date(task.start_time)
        : (endTime ? new Date(endTime.getTime() - FALLBACK_MIN * 60000) : now)

    let end
    if (endTime) {
        end = endTime
    } else {
        const durationMin = parseFloat(task.human_expected_duration_minutes) || 0
        const expectedEnd = new Date(start.getTime() + durationMin * 60000)
        const minEnd = new Date(now.getTime() + FALLBACK_MIN * 60000)
        end = expectedEnd.getTime() > minEnd.getTime() ? expectedEnd : minEnd
    }
    return { start, end }
}

const WIP_INFO_MESSAGE = 'Work In Process (WIP): jobs currently in ' +
    'production but not yet shipped. We deliberately limit WIP rather ' +
    'than letting machines race ahead - high WIP makes mistakes take ' +
    'longer to catch, making them much bigger, and harder to figure ' +
    'out what the mistake even was.'

const WHY_LOW_WIP_MESSAGE = 'Why we keep WIP low: the more jobs sitting ' +
    'around half-finished, the easier it is for one to get lost or ' +
    'misplaced in inventory before it moves on. And if a mistake does ' +
    'happen, a big pile of unfinished work means a lot more of it can ' +
    'go wrong before anyone notices - and by then it is much harder to ' +
    'tell exactly where or when the mistake actually happened.'

function round2(n) {
    return Math.round(parseFloat(n) * 100) / 100
}

function formatMinutes(totalMin) {
    const h = Math.floor(totalMin / 60)
    const m = Math.round(totalMin % 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
}

function aggregateBy(stats, key) {
    const map = {}
    for (const row of stats) {
        const label = row[key] || '(none)'
        if (!map[label]) {
            map[label] = {
                label,
                daily_schedule_hours: 0,
                in_progress_hours: 0,
                remaining_schedule_hours: 0,
                remaining_in_progress_hours: 0,
            }
        }
        map[label].daily_schedule_hours += parseFloat(row.daily_schedule_hours) || 0
        map[label].in_progress_hours += parseFloat(row.in_progress_hours) || 0
        map[label].remaining_schedule_hours += parseFloat(row.remaining_schedule_hours) || 0
        map[label].remaining_in_progress_hours += parseFloat(row.remaining_in_progress_hours) || 0
    }
    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label))
}

function efficiencyStyle(val) {
    if (val === null) return {}
    if (val <= 0.9) return { backgroundColor: '#d4edda' }
    if (val <= 1.1) return { backgroundColor: '#fff3cd' }
    return { backgroundColor: '#f8d7da' }
}

function weekLabel(isoMon) {
    const d = new Date(isoMon + 'T00:00:00')
    return `${d.getMonth() + 1}/${d.getDate()}`
}

class CapacityViewController extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            stats: [],
            equips: [],
            inProgress: [],
            employeeStats: { weeks: [], employees: [] },
            wipStats: null,
            paintColorCount: null,
            todaySchedule: [],
            queuedSchedule: [],
            completedDependencyIds: [],
        }
    }

    componentDidMount() {
        this.props.turnOnLoader()
        Promise.all([
            getCapacityStats(),
            getAllEquips(),
            getInProgressTasks(),
            getEmployeeWeeklyStats(),
            getWipStats(),
            getPaintColorCount(),
            getTodaysScheduleByEquip(),
            getQueuedScheduleByEquip(),
        ]).then(
            ([statsResult, equipsResult, inProgressResult, employeeStatsResult, wipResult, paintColorCountResult, todayScheduleResult, queuedScheduleResult]) => {
                this.setState({
                    stats: statsResult.data.stats,
                    equips: equipsResult.data.equips,
                    inProgress: inProgressResult.data.tasks,
                    employeeStats: employeeStatsResult.data,
                    wipStats: wipResult.data,
                    paintColorCount: paintColorCountResult.data.paintColorCount,
                    todaySchedule: todayScheduleResult.data.tasks,
                    queuedSchedule: queuedScheduleResult.data.tasks,
                    completedDependencyIds: queuedScheduleResult.data.completedDependencyIds,
                })
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.turnOffLoader()
                this.props.showError(error)
            },
        )
    }

    renderCurrentlyActive() {
        const { inProgress, stats } = this.state
        const activeNames = new Set(inProgress.map((t) => t.assign_to))
        const byAssignee = aggregateBy(stats, 'assign_to')
        const freeEmployees = byAssignee
            .map((r) => r.label)
            .filter((name) => name !== '(none)' && !activeNames.has(name))

        const now = Date.now()

        return (
            <>
                <h5 style={{ marginTop: '32px' }}>Currently Active</h5>
                <Row><Col><div style={{ overflowX: 'auto' }}><Table>
                    <thead>
                        <tr>
                            <th>Assigned To</th>
                            <th>Started By</th>
                            <th>Job</th>
                            <th>Task</th>
                            <th>Equipment</th>
                            <th>Expected</th>
                            <th>Elapsed</th>
                            <th>±Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inProgress.map((task) => {
                            const elapsedMin = (now - new Date(task.start_time)) / 60000
                            const expectedMin = parseFloat(task.human_expected_duration_minutes) || 0
                            const diffMin = elapsedMin - expectedMin
                            const overStyle = diffMin > 0
                                ? { color: '#dc3545', fontWeight: 600 }
                                : { color: '#6c757d' }
                            const sign = diffMin > 0 ? '+' : ''
                            const jobLabel = task.job_name || task.customer_name || task.quote_id.slice(0, 8)
                            return (
                                <tr key={task.id}>
                                    <td>{task.assign_to || '—'}</td>
                                    <td>{task.started_by_name && task.started_by_name !== task.assign_to ? task.started_by_name : '—'}</td>
                                    <td>
                                        <a href={'/scheduler/' + task.quote_id} rel="noreferrer">
                                            {jobLabel}
                                        </a>
                                    </td>
                                    <td>{[task.short_description, task.process, ...(Array.isArray(task.materials) ? task.materials : []).map(m => `${m.thickness} ${m.material}`)].filter(Boolean).join(', ')}</td>
                                    <td>{task.equip}</td>
                                    <td>{expectedMin ? formatMinutes(expectedMin) : '—'}</td>
                                    <td>{formatMinutes(elapsedMin)}</td>
                                    <td style={overStyle}>{sign}{formatMinutes(Math.abs(diffMin))}</td>
                                </tr>
                            )
                        })}
                        {freeEmployees.map((name) => (
                            <tr key={'free-' + name} style={{ opacity: 0.6 }}>
                                <td>{name}</td>
                                <td colSpan="7" style={{ fontStyle: 'italic' }}>Free</td>
                            </tr>
                        ))}
                        {inProgress.length === 0 && freeEmployees.length === 0 &&
                            <tr><td colSpan="8" className="text-center">No active tasks</td></tr>
                        }
                    </tbody>
                </Table></div></Col></Row>
            </>
        )
    }

    // Shared by renderMachineCalendar() and renderShipDateTable() so the
    // dependency-aware greedy simulation only runs once per render, not
    // once per section.
    computeCalendarBlocks() {
        const { todaySchedule, queuedSchedule, completedDependencyIds } = this.state
        const now = new Date()

        // Known (started/ended today) tasks per equip, real times via
        // computeTaskSpan.
        const byEquip = {}
        for (const task of todaySchedule) {
            const label = task.equip || '(none)'
            if (!byEquip[label]) {
                byEquip[label] = []
            }
            const { start, end } = computeTaskSpan(task, now)
            byEquip[label].push({ task, start, end, isQueued: false })
        }

        // Not-yet-started work: a dependency-aware greedy simulation
        // across all equips together, not an independent per-equip
        // stack. A task is only placed once every id in its own
        // `dependency` array is satisfied - either genuinely complete
        // already (completedDependencyIds from the server) or already
        // placed earlier in this same simulation (so a same-day chain
        // of dependent tasks, even across different machines, can still
        // schedule itself in one pass). Runs in the queue's own
        // priority/ship-date order within each pass, repeating passes
        // until nothing new becomes eligible - a task that never
        // becomes eligible (blocked on something outside today's
        // window) is simply left off the calendar, not guessed at.
        const cursorByEquip = {}
        for (const label of Object.keys(byEquip)) {
            const knownEnds = byEquip[label].map((b) => b.end.getTime())
            cursorByEquip[label] = new Date(Math.max(now.getTime(), ...knownEnds))
        }
        const completedIds = new Set(completedDependencyIds)
        for (const label of Object.keys(byEquip)) {
            for (const { task, isQueued } of byEquip[label]) {
                if (!isQueued && task.is_complete) {
                    completedIds.add(task.id)
                }
            }
        }
        const remaining = queuedSchedule.slice()
        let madeProgress = true
        while (remaining.length > 0 && madeProgress) {
            madeProgress = false
            for (let i = 0; i < remaining.length; i++) {
                const task = remaining[i]
                const dependency = task.dependency || []
                const eligible = dependency.every((depId) => completedIds.has(depId))
                if (!eligible) {
                    continue
                }
                const label = task.equip || '(none)'
                if (!byEquip[label]) {
                    byEquip[label] = []
                }
                if (!cursorByEquip[label]) {
                    cursorByEquip[label] = now
                }
                const cursor = cursorByEquip[label]
                // Explicit 15-minute default for queued/projected
                // placement specifically when a task's duration is 0 -
                // a real 0-length step would collapse the simulation
                // (and be invisible) rather than actually reserving
                // time on the machine. Real started/ended spans still
                // show 0 as given; this default is only for tasks that
                // haven't happened yet and need a placeholder duration.
                const durationMin = parseFloat(task.human_expected_duration_minutes) || 15
                const start = cursor
                const end = new Date(cursor.getTime() + durationMin * 60000)
                byEquip[label].push({ task, start, end, isQueued: true })
                cursorByEquip[label] = end
                completedIds.add(task.id)
                remaining.splice(i, 1)
                i--
                madeProgress = true
            }
        }

        return byEquip
    }

    renderMachineCalendar(byEquip) {
        const { equips } = this.state
        const windowStartMin = CALENDAR_WINDOW_START_HOUR * 60
        // Extends past midnight into the next day (e.g. 4am -> 4am the
        // next day = a full 24h window) - minutesSinceMidnight below is
        // a continuous coordinate (not wrapped to [0,1440)), so a task
        // spilling into tomorrow morning naturally lands past 1440
        // here without any special day-wrap handling needed.
        const windowEndMin = CALENDAR_WINDOW_END_HOUR * 60
        const windowMin = windowEndMin - windowStartMin
        const hours = []
        for (let h = CALENDAR_WINDOW_START_HOUR; h <= CALENDAR_WINDOW_END_HOUR; h++) {
            hours.push(h)
        }

        const now = new Date()
        const todayMidnight = new Date(now)
        todayMidnight.setHours(0, 0, 0, 0)
        const minutesSinceMidnight = (date) => (date.getTime() - todayMidnight.getTime()) / 60000

        const equipNames = equips.length
            ? equips.map((e) => e.name)
            : Object.keys(byEquip).sort()

        return (
            <>
                <h5 style={{ marginTop: '32px' }}>Machine Availability Today</h5>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <div style={{ width: '140px', flexShrink: 0 }} />
                    <div style={{ position: 'relative', flexGrow: 1, height: '14px' }}>
                        {hours.map((h) => {
                            // h can run past 24 (into "4am the next
                            // day") - wrap only for display, positioning
                            // above still uses the raw continuous h.
                            const displayHour = h % 24
                            return (
                                <div key={h} style={{
                                    position: 'absolute',
                                    left: ((h * 60 - windowStartMin) / windowMin) * 100 + '%',
                                    fontSize: '11px',
                                    color: '#6c757d',
                                    transform: 'translateX(-50%)',
                                }}>
                                    {displayHour % 12 === 0 ? 12 : displayHour % 12}{displayHour < 12 ? 'a' : 'p'}
                                </div>
                            )
                        })}
                    </div>
                </div>
                {equipNames.map((equipName) => {
                    const tasks = byEquip[equipName] || []
                    return (
                        <div key={equipName} style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '4px',
                            height: '36px',
                        }}>
                            <div style={{
                                width: '140px',
                                flexShrink: 0,
                                fontSize: '13px',
                                fontWeight: 600,
                                paddingRight: '8px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {equipName}
                            </div>
                            <div style={{
                                position: 'relative',
                                flexGrow: 1,
                                height: '28px',
                                backgroundColor: 'rgba(0,0,0,0.04)',
                                borderRadius: '4px',
                            }}>
                                {tasks.map(({ task, start, end, isQueued }) => {
                                    const startMin = minutesSinceMidnight(start)
                                    const endMin = minutesSinceMidnight(end)
                                    const clampedStart = Math.max(windowStartMin, Math.min(windowEndMin, startMin))
                                    const clampedEnd = Math.max(windowStartMin, Math.min(windowEndMin, endMin))
                                    const leftPct = ((clampedStart - windowStartMin) / windowMin) * 100
                                    // Minimum visible sliver so a 0-duration (or
                                    // fully-clamped-outside-window) block is still
                                    // visible/clickable rather than disappearing.
                                    const widthPct = Math.max(0.6, ((clampedEnd - clampedStart) / windowMin) * 100)
                                    const jobLabel = task.job_name || task.customer_name || task.quote_id.slice(0, 8)
                                    const durationMin = Math.round((end.getTime() - start.getTime()) / 60000)
                                    const timeFmt = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                                    const title = `${jobLabel} - ${[task.process, task.short_description].filter(Boolean).join(': ')} ` +
                                        `(${isQueued ? 'projected' : 'actual'}: ${timeFmt(start)}-${timeFmt(end)}, ~${durationMin} min)`
                                    return (
                                        <a
                                            key={task.id}
                                            href={'/scheduler/' + task.quote_id}
                                            title={title}
                                            style={{
                                                position: 'absolute',
                                                left: leftPct + '%',
                                                width: widthPct + '%',
                                                top: '2px',
                                                bottom: '2px',
                                                backgroundColor: colorForJob(task.quote_id),
                                                // Queued/projected work is visually
                                                // distinct (lighter + dashed) from
                                                // actual started/ended spans, since
                                                // it's a greedy estimate, not a fact.
                                                opacity: isQueued ? 0.35 : (task.is_complete ? 0.45 : 0.85),
                                                border: isQueued ? '1px dashed rgba(0,0,0,0.4)' : 'none',
                                                borderRadius: '3px',
                                                overflow: 'hidden',
                                                color: 'white',
                                                fontSize: '10px',
                                                lineHeight: '24px',
                                                paddingLeft: '3px',
                                                whiteSpace: 'nowrap',
                                                textDecoration: 'none',
                                            }}
                                        >
                                            {jobLabel}
                                        </a>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </>
        )
    }

    renderShipDateTable(byEquip) {
        const byJob = {}
        for (const label of Object.keys(byEquip)) {
            for (const { task, end } of byEquip[label]) {
                if (!byJob[task.quote_id]) {
                    byJob[task.quote_id] = {
                        quoteId: task.quote_id,
                        jobLabel: task.job_name || task.customer_name || task.quote_id.slice(0, 8),
                        customerShipDate: task.expected_ship_date || null,
                        actualShipDate: end,
                    }
                } else {
                    const job = byJob[task.quote_id]
                    if (!job.customerShipDate && task.expected_ship_date) {
                        job.customerShipDate = task.expected_ship_date
                    }
                    if (end.getTime() > job.actualShipDate.getTime()) {
                        job.actualShipDate = end
                    }
                }
            }
        }

        const jobs = Object.values(byJob).sort(
            (a, b) => a.actualShipDate.getTime() - b.actualShipDate.getTime(),
        )

        if (jobs.length === 0) {
            return null
        }

        const formatActual = (d) => d.toLocaleDateString([], { month: 'numeric', day: 'numeric' }) +
            ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

        // customerShipDate is a raw "YYYY-MM-DD" string from the server -
        // formatted to match MAT Ship Date's m/d style (no time, since
        // it's a plain date with no time component to show).
        const formatCustomer = (isoDateStr) => new Date(isoDateStr + 'T00:00:00')
            .toLocaleDateString([], { month: 'numeric', day: 'numeric' })

        // Compare by calendar date only (customerShipDate has no time
        // component) - zero-padded ISO "YYYY-MM-DD" strings compare
        // correctly with plain string comparison, no Date math needed.
        const isLate = (job) => {
            if (!job.customerShipDate) {
                return false
            }
            const d = job.actualShipDate
            const actualDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            return actualDateStr > job.customerShipDate
        }

        return (
            <>
                <h5 style={{ marginTop: '32px' }}>
                    Ship Dates - Customer Promised vs. MAT (Machine Availability Today)
                </h5>
                <Row><Col><div style={{ overflowX: 'auto' }}><Table style={{ marginBottom: '32px' }}>
                    <thead>
                        <tr>
                            <th></th>
                            {jobs.map((job) => (
                                <th key={job.quoteId} style={{ minWidth: '200px' }}>
                                    <a href={'/scheduler/' + job.quoteId} rel="noreferrer">
                                        {job.jobLabel}
                                    </a>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Customer Ship Date</strong></td>
                            {jobs.map((job) => (
                                <td key={job.quoteId} style={{ minWidth: '200px' }}>
                                    {job.customerShipDate ? <Label><span text={dateOffsetText(new JustDate(job.customerShipDate))}>
                                        {formatCustomer(job.customerShipDate)}
                                    </span></Label> : '—'}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td><strong>MAT Ship Date</strong></td>
                            {jobs.map((job) => (
                                <td key={job.quoteId} style={{ minWidth: '200px' }}>
                                    <Label><span text={dateOffsetText(new JustDate(job.actualShipDate))}>
                                        {formatActual(job.actualShipDate)}
                                    </span></Label>
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td><strong>Late?</strong></td>
                            {jobs.map((job) => (
                                <td key={job.quoteId} style={{ minWidth: '200px', ...(isLate(job) ? { color: '#dc3545', fontWeight: 600 } : {}) }}>
                                    {isLate(job) ? 'X' : ''}
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </Table></div></Col></Row>
            </>
        )
    }

    renderEmployeeStats() {
        const { employeeStats } = this.state
        const { weeks, employees } = employeeStats
        if (!weeks || weeks.length === 0) return null

        const dash = <span style={{ opacity: 0.4 }}>—</span>

        return (
            <>
                <h5 style={{ marginTop: '32px' }}>Employee Weekly Stats</h5>
                <Row><Col><div style={{ overflowX: 'auto' }}><Table style={{ minWidth: '1500px', fontSize: '0.82rem' }}>
                    <thead>
                        <tr>
                            <th rowSpan="2" style={{ position: 'sticky', left: 0, zIndex: 1, whiteSpace: 'nowrap' }}>
                                Employee
                            </th>
                            {weeks.map((w) => (
                                <th key={w} colSpan="10" className="text-center">
                                    Week of {weekLabel(w)}
                                </th>
                            ))}
                            <th rowSpan="2" className="text-center">Tasks<br/>Total</th>
                        </tr>
                        <tr>
                            {weeks.map((w) => (
                                <React.Fragment key={w}>
                                    <th>Days</th>
                                    <th>Eff.</th>
                                    <th>Util%</th>
                                    <th>Task h</th>
                                    <th>On-site h</th>
                                    <th title="Breaks >10 min">&gt;10m</th>
                                    <th title="Breaks >25 min">&gt;25m</th>
                                    <th title="Breaks >1 hour">&gt;1h</th>
                                    <th title="Breaks >4 hours">&gt;4h</th>
                                    <th title="Overnight gaps >8 hours">&gt;8h</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {employees.map((emp) => (
                            <tr key={emp.name}>
                                <td style={{ position: 'sticky', left: 0, whiteSpace: 'nowrap' }}>
                                    {emp.name}
                                </td>
                                {emp.weeks.map((w, wi) => (
                                    <React.Fragment key={wi}>
                                        <td>{w.days > 0 ? w.days : dash}</td>
                                        <td style={efficiencyStyle(w.efficiency)}>
                                            {w.efficiency !== null ? w.efficiency.toFixed(2) : dash}
                                        </td>
                                        <td>{w.utilization !== null ? w.utilization + '%' : dash}</td>
                                        <td>{w.task_hrs > 0 ? w.task_hrs : dash}</td>
                                        <td>{w.on_site_hrs > 0 ? w.on_site_hrs : dash}</td>
                                        <td>{w.breaks_10m || dash}</td>
                                        <td>{w.breaks_25m || dash}</td>
                                        <td>{w.breaks_1h || dash}</td>
                                        <td>{w.breaks_4h || dash}</td>
                                        <td>{w.breaks_8h || dash}</td>
                                    </React.Fragment>
                                ))}
                                <td className="text-center">{emp.total_tasks}</td>
                            </tr>
                        ))}
                        {employees.length === 0 &&
                            <tr><td colSpan={1 + weeks.length * 10 + 1} className="text-center">No data</td></tr>
                        }
                    </tbody>
                </Table></div></Col></Row>
            </>
        )
    }

    render() {
        const { stats, equips, wipStats, paintColorCount } = this.state
        const equipQuantity = {}
        equips.forEach((e) => { equipQuantity[e.name] = e.quantity })

        const byMachine = aggregateBy(stats, 'equip')
        const byAssignee = aggregateBy(stats, 'assign_to')

        return (
            <Container>
                <h3 style={{ marginTop: '20px', marginBottom: '16px' }}>Capacity</h3>

                <div style={{ marginBottom: '16px' }}>
                    <strong>Work In Process (WIP): {wipStats === null ? '—' : wipStats.wip}</strong>
                    {' '}
                    <InfoButton id="wip-info" message={WIP_INFO_MESSAGE} />
                    {' '}
                    <InfoButton id="wip-why-info" message={WHY_LOW_WIP_MESSAGE} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <strong>Signs In Process (SIP): {wipStats === null ? '—' : wipStats.sip}</strong>
                    {' '}
                    <InfoButton id="sip-info" message={
                        'Count of individual signs currently in production, not job ' +
                        'count - a job with 50 signs and a job with 1 sign both count ' +
                        'as "1" toward plain WIP, which hides exactly the size ' +
                        'variance that metric should be reflecting.'
                    } />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <strong>Square Inches In Process (SQINIP): {wipStats === null ? '—' : Math.round(wipStats.sqinip).toLocaleString()}</strong>
                    {' '}
                    <InfoButton id="sqinip-info" message={
                        'Total sign area (height x width x quantity) currently in ' +
                        'production. The unit that most directly tracks physical ' +
                        'storage/space tied up - more directly than job count, sign ' +
                        'count, or duration would.'
                    } />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <strong>Days In Progress (DIP): avg {wipStats === null ? '—' : wipStats.avgDip.toFixed(1)}, max {wipStats === null ? '—' : wipStats.maxDip}</strong>
                    {' '}
                    <InfoButton id="dip-info" message={
                        'Average and longest number of days since a job currently ' +
                        'in production entered "In Production" - the cycle-time ' +
                        'half of Little\'s Law (WIP = Throughput x Cycle Time). ' +
                        'Rising average DIP means jobs are sitting longer, not just ' +
                        'that there are more of them.'
                    } />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <strong>
                        WIP Value: {wipStats === null ? '—' : '$' + Math.round(wipStats.wipDollars).toLocaleString()}
                        {wipStats && wipStats.impliedDailyThroughputDollars !== null &&
                            ` (implied throughput: ~$${Math.round(wipStats.impliedDailyThroughputDollars).toLocaleString()}/day)`}
                    </strong>
                    {' '}
                    <InfoButton id="wip-dollars-info" message={
                        'Total dollar value of quotes currently in production. ' +
                        'Implied daily throughput is derived from Little\'s Law ' +
                        '(Throughput = WIP / Cycle Time) using WIP value divided by ' +
                        'average Days In Progress, rather than a separately-tracked ' +
                        '"$ shipped per day" history.'
                    } />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <strong>Unique Paint Colors In Production: {paintColorCount === null ? '—' : paintColorCount}</strong>
                    {' '}
                    <InfoButton id="paint-color-info" message={
                        'Total distinct paint colors (#RGBA values) needed ' +
                        'across every job currently in production. Fewer ' +
                        'distinct colors in flight at once means fewer ' +
                        'paint-booth changeovers.'
                    } />
                </div>

                <h5>By Machine</h5>
                <Row><Col><div style={{'overflowX': 'auto'}}><Table style={{ marginBottom: '32px' }}>
                    <thead>
                        <tr>
                            <th rowSpan="2">Machine</th>
                            <th rowSpan="2" style={{ width: '60px' }}>Qty</th>
                            <th colSpan="4" className="text-center">Total</th>
                            <th colSpan="4" className="text-center">Per Machine</th>
                        </tr>
                        <tr>
                            <th>Daily Schedule</th>
                            <th>In Progress</th>
                            <th>Remaining (Schedule)</th>
                            <th>Remaining (In Progress)</th>
                            <th>Daily Schedule</th>
                            <th>In Progress</th>
                            <th>Remaining (Schedule)</th>
                            <th>Remaining (In Progress)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {byMachine.map((row) => {
                            const qty = equipQuantity[row.label] || null
                            const perUnit = (v) => qty ? round2(v / qty) : '—'
                            return (
                                <tr key={row.label}>
                                    <td>{row.label}</td>
                                    <td>{qty ?? '—'}</td>
                                    <td>{round2(row.daily_schedule_hours)}</td>
                                    <td>{round2(row.in_progress_hours)}</td>
                                    <td>{round2(row.remaining_schedule_hours)}</td>
                                    <td>{round2(row.remaining_in_progress_hours)}</td>
                                    <td>{perUnit(row.daily_schedule_hours)}</td>
                                    <td>{perUnit(row.in_progress_hours)}</td>
                                    <td>{perUnit(row.remaining_schedule_hours)}</td>
                                    <td>{perUnit(row.remaining_in_progress_hours)}</td>
                                </tr>
                            )
                        })}
                        {byMachine.length === 0 &&
                            <tr><td colSpan="10" className="text-center">No data</td></tr>
                        }
                    </tbody>
                </Table></div></Col></Row>

                <h5>By Assignee</h5>
                <Row><Col><div style={{'overflowX': 'auto'}}><Table>
                    <thead>
                        <tr>
                            <th>Assignee</th>
                            <th>Daily Schedule Hrs</th>
                            <th>In Progress Hrs</th>
                            <th>Remaining (Schedule)</th>
                            <th>Remaining (In Progress)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {byAssignee.map((row) => (
                            <tr key={row.label}>
                                <td>{row.label}</td>
                                <td>{round2(row.daily_schedule_hours)}</td>
                                <td>{round2(row.in_progress_hours)}</td>
                                <td>{round2(row.remaining_schedule_hours)}</td>
                                <td>{round2(row.remaining_in_progress_hours)}</td>
                            </tr>
                        ))}
                        {byAssignee.length === 0 &&
                            <tr><td colSpan="5" className="text-center">No data</td></tr>
                        }
                    </tbody>
                </Table></div></Col></Row>

                {this.renderCurrentlyActive()}
                {(() => {
                    const byEquip = this.computeCalendarBlocks()
                    return <>
                        {this.renderMachineCalendar(byEquip)}
                        {this.renderShipDateTable(byEquip)}
                    </>
                })()}
                {this.renderEmployeeStats()}
            </Container>
        )
    }
}

export default CapacityViewController
